import { useEffect, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import api from "@/lib/api";
import NpdQuoteSheet from "@/components/NpdQuoteSheet";

// Reference data (settings/customers/units) is the same for every quote on a page,
// so fetch it once and cache module-wide — keeps repeat downloads instant.
let _refsCache = null;
async function loadRefs() {
  if (_refsCache) return _refsCache;
  const [s, c, u] = await Promise.all([
    api.get("/settings/public").catch(() => ({ data: {} })),
    api.get("/customers").catch(() => ({ data: [] })),
    api.get("/units").catch(() => ({ data: [] })),
  ]);
  _refsCache = { settings: s.data || {}, customers: c.data || [], units: u.data || [] };
  return _refsCache;
}

/**
 * In-page quote → PDF, no new tab / no navigation. Set `npd` (a fully-serialized costing
 * row) to trigger: it renders the quote sheet off-screen, rasterises it and downloads the
 * PDF, then calls onDone. Reuses the parent's already-loaded row data, so it's fast.
 */
export default function QuotePdfDownloader({ npd, onDone }) {
  const [refs, setRefs] = useState(_refsCache);
  const ref = useRef(null);
  const ranFor = useRef(null);

  useEffect(() => {
    if (!npd) { ranFor.current = null; return; }
    if (!refs) {
      loadRefs().then(setRefs).catch(() => { toast.error("Could not load quote data"); onDone && onDone(); });
      return;
    }
    if (ranFor.current === npd.npd_id) return;
    ranFor.current = npd.npd_id;
    let cancelled = false;
    (async () => {
      // Let NpdQuoteSheet mount first.
      await new Promise((r) => setTimeout(r, 120));
      const el = ref.current;
      if (!el || cancelled) return;
      // The drawing screenshot is fetched async and only appears in the DOM (as a blob: URL
      // image) once loaded. Poll until every image is loaded — and, when this quote has a
      // screenshot, until that blob image is actually present and decoded — so html2canvas
      // never captures before the drawing lands. Safety-capped so it can't hang.
      const expectShot = !!npd.drawing_screenshot_path;
      const deadline = Date.now() + 5000;
      while (!cancelled && Date.now() < deadline) {
        const imgs = Array.from(el.querySelectorAll("img"));
        const allLoaded = imgs.length > 0 && imgs.every((i) => i.complete && i.naturalWidth > 0);
        const shotReady = imgs.some((i) =>
          (i.currentSrc || i.src || "").startsWith("blob:") && i.complete && i.naturalWidth > 0
        );
        if (allLoaded && (!expectShot || shotReady)) break;
        await new Promise((r) => setTimeout(r, 150));
      }
      if (cancelled) return;
      try {
        const canvas = await html2canvas(el, { scale: 1.5, useCORS: true, backgroundColor: "#ffffff", logging: false });
        const imgData = canvas.toDataURL("image/jpeg", 0.9);
        const pdf = new jsPDF("p", "mm", "a4");
        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();
        const imgW = pageW;
        const imgH = (canvas.height * imgW) / canvas.width;
        let heightLeft = imgH;
        let position = 0;
        pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
        heightLeft -= pageH;
        while (heightLeft > 0) {
          position -= pageH;
          pdf.addPage();
          pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
          heightLeft -= pageH;
        }
        pdf.save(`${npd.npd_number || "NPD"}_${(npd.part_number || "part").replace(/\//g, "-")}.pdf`);
      } catch (e) {
        toast.error("Could not generate PDF");
      } finally {
        if (!cancelled) onDone && onDone();
      }
    })();
    return () => { cancelled = true; };
  }, [npd, refs]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!npd) return null;
  const customerName = refs?.customers.find((c) => c.customer_id === npd.customer_id)?.name || "—";
  const unit = refs?.units.find((u) => u.unit_id === npd.unit_id) || null;

  return (
    <>
      {/* Visible progress chip */}
      <div className="fixed bottom-4 right-4 z-50 bg-slate-900 text-white text-xs rounded-md px-3 py-2 flex items-center gap-2 shadow-lg" data-testid="pdf-preparing-chip">
        <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
        Preparing PDF…
      </div>
      {/* Off-screen render target for html2canvas */}
      {refs && (
        <div style={{ position: "fixed", left: "-10000px", top: 0, width: "210mm", background: "#fff" }} aria-hidden>
          <div ref={ref} className="bg-white p-6">
            <NpdQuoteSheet npd={npd} customerName={customerName} settings={refs.settings} unit={unit} />
          </div>
        </div>
      )}
    </>
  );
}
